import React, { useContext } from "react";

import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/src/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { Skeleton } from "@/src/components/ui/skeleton";
import { cn } from "@/src/lib/utils";

import LiveConfirmAction from "./LiveConfirmAction";
import {
  formatClock,
  formatRecordedDuration,
  getRemainingSeconds,
} from "./liveFormat";
import LiveReplayRange from "./LiveReplayRange";
import ContentContext from "../../contexts/ContentContext";
import DataTableStatusDot from "../dataTable/DataTableStatusDot";
import Player from "../player/Player";

import type { PlayerAPI } from "okast-player";
import type { JSX, ReactNode } from "react";

import type {
  LiveReplayRange as ReplayRange,
  UseLiveRegieResult,
} from "./live.types";
import type { GetMediaResponse } from "../../api/MediaAPI.types";
import type { LiveView } from "../../utils/liveView";
import type { StatusTone } from "../dataTable/dataTable.types";

// Muted before the source loads, so the on-air autoplay is allowed and silent.
function mutePlayer(player: PlayerAPI): void {
  player.mute();
}

interface LivePreviewCardProps {
  regie: UseLiveRegieResult;
  range: ReplayRange;
  isRangeModified: boolean;
  onRangeChange: (range: ReplayRange) => void;
}

interface TileProps {
  label: string;
  value: string;
  dot?: ReactNode;
}

const QUALITY_TONE: Partial<
  Record<NonNullable<LiveView["quality"]>, StatusTone>
> = {
  healthy: "success",
  degraded: "warning",
  outage: "error",
};

const PLAYER_STATES = [
  "readyToAir",
  "publishing",
  "live",
  "degraded",
  "replayAvailable",
  "replayCreated",
  "ended",
];

function getObsKey(state: string, isConnected: boolean): string {
  if (isConnected) return "liveRegie_obsConnected";
  return state === "signalLost"
    ? "liveRegie_obsDisconnected"
    : "liveRegie_obsWaiting";
}

const PLACEHOLDER_TITLE_STATES = ["waitingSignal", "signalLost"];

function Tile({ label, value, dot }: TileProps): JSX.Element {
  return (
    <div className="bg-muted/50 rounded-lg border p-3">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="flex items-center gap-2 text-sm font-semibold">
        {dot}
        {value}
      </div>
    </div>
  );
}

function LivePreviewCard({
  regie,
  range,
  isRangeModified,
  onRangeChange,
}: LivePreviewCardProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { item } = useContext<{ item: GetMediaResponse }>(ContentContext);
  const { view, settings, actions, isMutating, now } = regie;
  const { phase, state } = view;
  const isSignalLost = state === "signalLost";
  const isTermine = phase === "termine";
  const showPlayer = PLAYER_STATES.includes(state);
  const isOnAir = phase === "antenne";
  const showTiles = phase === "connexion" || phase === "antenne";
  const showRange = state === "replayAvailable" || state === "replayCreated";
  const remainingSeconds = getRemainingSeconds(view.liveEnd, now);

  function getPlaceholder(): { title: string; text: string } {
    return {
      title: PLACEHOLDER_TITLE_STATES.includes(state)
        ? t(`liveRegie_placeholder_${state}_title`)
        : "",
      text: t(`liveRegie_placeholder_${state}_text`),
    };
  }

  function renderQualityValue(): string {
    if (isSignalLost) return t("quality_no_data");
    if (view.quality === null || view.quality === "no_data") {
      return state === "live" || state === "checkingSignal"
        ? t("liveRegie_qualityChecking")
        : t("quality_no_data");
    }
    return t(`quality_${view.quality}`);
  }

  function renderTiles(): ReactNode {
    if (!showTiles) return null;
    const isEncoderWaiting =
      state === "waitingSignal" || state === "provisioning";
    const isConnected =
      !isSignalLost && !isEncoderWaiting && state !== "provisioningFailed";
    const qualityTone =
      (!isSignalLost && view.quality && QUALITY_TONE[view.quality]) ||
      "neutral";
    let encoderTone: StatusTone = "neutral";
    if (isConnected) encoderTone = "success";
    else if (isSignalLost) encoderTone = "error";
    const thirdTile =
      phase === "antenne" ? (
        <Tile
          label={t("liveRegie_tileRemaining")}
          value={
            remainingSeconds === null
              ? "—"
              : t("liveRegie_minutes", {
                  minutes: Math.ceil(remainingSeconds / 60),
                })
          }
        />
      ) : (
        <Tile
          label={t("liveRegie_tileDuration")}
          value={t("liveRegie_minutes", {
            minutes: Math.round(settings.durationSeconds / 60),
          })}
        />
      );

    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile
          dot={<DataTableStatusDot className="size-2" tone={qualityTone} />}
          label={t("status_quality")}
          value={renderQualityValue()}
        />
        <Tile
          label={t("liveRegie_tileObs")}
          value={t(getObsKey(state, isConnected))}
          dot={
            <DataTableStatusDot
              className={cn("size-2", isEncoderWaiting && "animate-pulse")}
              tone={encoderTone}
            />
          }
        />
        {thirdTile}
      </div>
    );
  }

  function renderBody(): ReactNode {
    if (showPlayer) {
      return (
        <div className="overflow-hidden rounded-lg bg-black">
          {/* okast-player reads autoPlay at init only: remount when going on or off air. */}
          <Player
            key={isOnAir ? "on-air" : "paused"}
            autoPlay={isOnAir}
            item={item}
            onInit={isOnAir ? mutePlayer : undefined}
          />
        </div>
      );
    }
    if (state === "stopping") {
      return (
        <div className="bg-muted flex aspect-video flex-col items-center justify-center gap-3 rounded-lg p-6 text-center">
          <Loader2 aria-hidden="true" className="size-6 animate-spin" />
          <p className="text-sm font-medium">
            {t("liveRegie_placeholder_stopping_title")}
          </p>
          <p className="text-muted-foreground text-sm">
            {t("liveRegie_placeholder_stopping_text")}
          </p>
        </div>
      );
    }
    const placeholder = getPlaceholder();
    return (
      <div className="bg-muted flex aspect-video flex-col items-center justify-center gap-2 rounded-lg p-6 text-center">
        {placeholder.title ? (
          <p className="text-sm font-medium">{placeholder.title}</p>
        ) : null}
        <p className="text-muted-foreground text-sm">{placeholder.text}</p>
      </div>
    );
  }

  function renderRange(): ReactNode {
    if (state === "stopping") {
      return <Skeleton className="h-10 w-full" />;
    }
    if (!showRange) return null;
    const isCreated = state === "replayCreated";

    return (
      <LiveReplayRange
        onChange={onRangeChange}
        totalSeconds={settings.durationSeconds}
        value={range}
      >
        {isCreated ? (
          <LiveConfirmAction
            confirmLabel={t("liveRegie_updateReplayConfirm")}
            disabled={!isRangeModified || Boolean(isMutating.updateReplay)}
            label={t("liveRegie_updateReplay")}
            onConfirm={() => actions.updateReplay(range)}
            title={t("liveRegie_updateReplayConfirmTitle")}
            description={t("liveRegie_replayConfirmDescription", {
              start: formatClock(range.start),
              end: formatClock(range.end),
              duration: formatRecordedDuration(range.end - range.start),
            })}
          />
        ) : null}
      </LiveReplayRange>
    );
  }

  function renderBadge(): ReactNode {
    if (isTermine) {
      return state === "stopping" ? null : (
        <span className="text-muted-foreground text-sm">
          {formatRecordedDuration(settings.durationSeconds)}
        </span>
      );
    }
    return phase === "antenne" ? (
      <Badge variant="success">{t("liveRegie_publicBadge")}</Badge>
    ) : (
      <Badge variant="neutral">{t("liveRegie_privateBadge")}</Badge>
    );
  }

  return (
    <Card className="gap-4 py-4">
      <CardHeader className="flex flex-row items-center justify-between gap-2 px-4">
        <CardTitle>
          {t(isTermine ? "liveRegie_recordingTitle" : "liveRegie_previewTitle")}
        </CardTitle>
        {renderBadge()}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-4">
        {renderBody()}
        {renderTiles()}
        {renderRange()}
      </CardContent>
    </Card>
  );
}

export default LivePreviewCard;
